"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProxyClient = void 0;
/* eslint-disable @typescript-eslint/naming-convention */
const Client_1 = require("./Client");
const Errors_1 = require("./Errors");
/**
 * Creates an instance of Proxy Client
 *
 * This is used to do all the request relative to the Proxy API.
 *
 * @public
 */
class ProxyClient {
    /**
     * Creates an instance of Proxy Client.
     *
     * The constructor creates a instance of the Client class:
     * - The client instance is created with the apiBaseURLDirectBook parameter.
     *
     * @param apiBaseURL - The base URL of the proxy.
     * @param domainKey - The domain key for the proxy.
     * @param headerRefererOverride - This is a OPTIONAL parameter: header referer override for the proxy. (use case: when you want to change the referer for local developpement)
     *
     * @remarks
     * The parameters `headerRefererOverride` must only be use for local developpement.
     */
    constructor(apiBaseURL, domainKey, headerRefererOverride) {
        this.apiBaseURL = apiBaseURL;
        this.domainKey = domainKey;
        this.headerRefererOverride = headerRefererOverride;
        this.client = new Client_1.Client(this.apiBaseURL);
    }
    /**
     * Make an API call to get a payment link
     *
     * @param locationID - the ID of location
     * @param body - structure RequestPaymentLink contain information for the requesting a payment link
     *
     * @returns an object of type APIResponsePaymentLink which contains a link for doing the payment
     *
     * @remarks This fields are required:
     * - packages[].id
     * - packages[].Days[].attendees
     * - arrivalDate
     * - primaryContact.email
     * - XRedirectPath
     *
     * @remarks You can provide a XLanguageCode to handle the packageDetails and Sertifi translate
     *
     * @throws `Error` class contain information about the failed request
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ProxyClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getPaymentLinkDirectBook("locationID", {...RequestPaymentLink}).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    getPaymentLinkDirectBook(locationID, body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Define the request init options
            const req = {
                method: "POST",
                headers: {
                    "X-Galaxy-Key": this.domainKey
                }
            };
            if (this.headerRefererOverride) {
                req.headers["X-Referer-Override"] = this.headerRefererOverride;
            }
            // Use the client call method
            const [response, error] = yield this.client.call(`/directbook/v1/payment/${locationID}/link`, req, body);
            // Handle error
            if (error !== null) {
                throw new Errors_1.ClientError(error === null || error === void 0 ? void 0 : error.code, error === null || error === void 0 ? void 0 : error.message);
            }
            return response;
        });
    }
    /**
     * Make an API call to get a payment prices.
     * This method calcules all the prices informations (packages, addOns, taxes, fee, total...). He will return this information, and this is suppose to be use in the frontend side.
     * The method prices calculation is reused inside the proxy for the "payment process" during the paymentLink call (even if the information is in the cache, it will be recalculate always by the paymentLink call)
     *
     * @param locationID - the ID of location
     * @param body - structure RequestPaymentPrices contain information for the requesting a payment prices
     * @param queries - (Optional) This is object contains all the possible queries: useCache (boolean) and languageCode (string)
     * @returns an object of type APIResponsePaymentPrices which contains the prices information about the package
     *
     * @remarks useCache - This is a Boolean. When set to FALSE (which is the default situation), the price will always be recalculated (and the response will be stored in the cache). When set to TRUE, the proxy will check the cache. If the element is found, it will be retrieved from the cache; otherwise, the prices will be recalculated. The purpose is to deliver a fast response when necessary. The cache duration is (maybe different/change) few minutes.
     * @remarks languageCode - This is a String for handling the translation (no default value is define)
     *
     * @remarks This fields are required:
     * - packages[].id
     * - packages[].Days[].attendees
     *
     * @remarks packages[].Days[].addOns[].quantity =\> An addOn with a quantity of "zero" will be ignored, but if the quantity is below zero, this will raise an error.
     *
     * @throws `Error` class contain information about the failed request
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ProxyClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getPaymentPricesDirectBook("locationID" {...RequestPaymentPrices}, {useCache: false, languageCode: "FR"}).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    getPaymentPricesDirectBook(locationID, body, queries = { useCache: false, languageCode: "" }) {
        return __awaiter(this, void 0, void 0, function* () {
            // Define the request init options
            const req = {
                method: "POST",
                headers: {
                    "X-Galaxy-Key": this.domainKey
                }
            };
            if (this.headerRefererOverride) {
                req.headers["X-Referer-Override"] = this.headerRefererOverride;
            }
            if (queries.useCache === true) {
                queries.useCache = true;
            }
            else {
                queries.useCache = false;
            }
            // Use the client call method
            const [response, error] = yield this.client.call(
            // eslint-disable-next-line max-len
            `/directbook/v1/payment/${locationID}/prices?useCache=${queries.useCache}&languageCode=${queries.languageCode}`, req, body);
            // Handle error
            if (error !== null) {
                throw new Errors_1.ClientError(error.Code, error.Message);
            }
            return response;
        });
    }
    /**
     * Make an API call to get a payment status
     *
     * @param folderID - contain the sertifi folderID
     * @param paymentID - contain the sertifi paymentID
     * @param xObjectID - contain the sertifi xObjectID (use to retrieve Sertifi credentials throught proxyDomain)
     * @param xObjectType - contain the sertifi xObjectType (use to retrieve Sertifi credentials throught proxyDomain)
     *
     * @returns an object of type APIResponsePaymentStatus which contains information about the payment
     *
     * @throws `Error` class contain information about the failed request
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ProxyClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getPaymentStatusDirectBook("folderID", "paymentID", "xObjectID", "xObjectType").then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    getPaymentStatusDirectBook(folderID, paymentID, xObjectID, xObjectType) {
        return __awaiter(this, void 0, void 0, function* () {
            // Define the request init options
            const req = {
                method: "GET",
                headers: {
                    "X-Galaxy-Key": this.domainKey
                }
            };
            if (this.headerRefererOverride) {
                req.headers["X-Referer-Override"] = this.headerRefererOverride;
            }
            // Use the client call method
            const [response, error] = yield this.client.call(
            // eslint-disable-next-line max-len
            `/directbook/v1/payment/status?folderId=${folderID}&paymentId=${paymentID}&xObjectID=${xObjectID}&xObjectType=${xObjectType}`, req);
            // Handle error
            if (error !== null) {
                throw new Errors_1.ClientError(error.Code, error.Message);
            }
            return response;
        });
    }
    /**
     * Retrieve API Call to AreaSearch Shop&Book
     *
     * @param body - contain body of AreaSearch API Call
     *
     * @returns an object of type APIResponseAreaSearch which contains information about the areaSearch
     *
     * @throws `Error` class contain information about the failed request
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ProxyClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.apiShopBookAreaSearch(body).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    apiShopBookAreaSearch(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Define the request init options
            const req = {
                method: "POST",
                headers: {
                    "X-Galaxy-Key": this.domainKey
                }
            };
            if (this.headerRefererOverride) {
                req.headers["X-Referer-Override"] = this.headerRefererOverride;
            }
            // Use the client call method
            const [response, error] = yield this.client.call("/directbook/v1/api/packagesearch", req, body);
            // Handle error
            if (error !== null) {
                throw new Errors_1.ClientError(error.Code, error.Message);
            }
            return response;
        });
    }
    /**
     * Retrieve API Call to PackageDetails Shop&Book
     *
     * @param body - contain body of PackageDetails API Call
     *
     * @returns an object of type APIResponsePackageDetails which contains information about the areaSearch
     *
     * @throws `Error` class contain information about the failed request
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ProxyClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.apiShopBookPackageDetails(body).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    apiShopBookPackageDetails(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Define the request init options
            const req = {
                method: "POST",
                headers: {
                    "X-Galaxy-Key": this.domainKey
                }
            };
            if (this.headerRefererOverride) {
                req.headers["X-Referer-Override"] = this.headerRefererOverride;
            }
            // Use the client call method
            const [response, error] = yield this.client.call("/directbook/v1/api/packagedetails", req, body);
            // Handle error
            if (error !== null) {
                throw new Errors_1.ClientError(error.Code, error.Message);
            }
            return response;
        });
    }
    /**
     * Retrieve API Call to Meeting RFP
     *
     * @param body - contain body of Meeting RFP API Call
     *
     * @returns nothing
     *
     * @throws `Error` class contain information about the failed request
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ProxyClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.apiShopBookPackageDetails(body).then((response) => {
     *     console.log(response) // Empty because There is no API response
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    apiRFPMeeting(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Define the request init options
            const req = {
                method: "POST",
                headers: {
                    "X-Galaxy-Key": this.domainKey
                }
            };
            if (this.headerRefererOverride) {
                req.headers["X-Referer-Override"] = this.headerRefererOverride;
            }
            // Use the client call method
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [_, error] = yield this.client.call("/directbook/v1/api/meeting", req, body);
            // Handle error
            if (error !== null) {
                throw new Errors_1.ClientError(error.Code, error.Message);
            }
        });
    }
}
exports.ProxyClient = ProxyClient;
//# sourceMappingURL=ClientProxy.js.map